/* eslint-disable camelcase */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom'
import {
  IconButton,
  Typography,
  Tooltip,
  Stack,
  Autocomplete,
  TextField,
  Box,
  Button,
  Alert,
  CircularProgress,
  Chip,
  AlertTitle,
} from '@mui/material'
import { ArrowBack } from '@material-ui/icons'

import { CONVERSION_EVENTS_OBJECT, CONVERSION_REPORT_ROUTES } from 'constants/index'
import { useTheme } from '@mui/material/styles'
import useMetaEventsSave from 'mutations/useMetaEventsSave'
import useSnackbar from 'hooks/useSnackbar'
import useGetEventDetails from 'queries/use-get-event-details'
import { Skeletons } from 'components/Skeletons'
import useGetDepartmentList from 'queries/use-get-departments-list'

const allDeps = {
  id: -1,
  label: 'Todos os departamentos',
  uuid: '00000000-0000-0000-0000-000000000000',
}

const eventsListPath = `/relatorios/conversion/${CONVERSION_REPORT_ROUTES.MANAGEMENT_EVENTS}`

function EditEvents() {
  const [channels, _] = useState([{ id: 11, name: 'Bot' }])
  const [departments, setDepartments] = useState([])
  const navigate = useNavigate()
  const { state, pathname } = useLocation()

  const theme = useTheme()
  const { eventId, eventType } = useParams()

  // const { data: channelsList, isLoading: loadingChannel } = useGetChannels()
  const { mutateAsync, isLoading, isError, isSuccess } = useMetaEventsSave()
  const { data: eventDetails, isLoading: loadingDetails } = useGetEventDetails(state.metaEventId)
  const { data, isLoading: loadingDepartments } = useGetDepartmentList()

  const normalizeDepartments = useCallback((departments) => {
    if (departments && departments.length > 0) {
      const options = departments.map((department, index) => ({
        id: index,
        uuid: department.Id,
        label: department.Name,
      }))
      return options.sort((optionA, optionB) => optionA.label.localeCompare(optionB.label))
    }
    return []
  }, [])

  const departmentsList = useMemo(() => {
    const value = data?.HiBot || []
    return normalizeDepartments(value)
  }, [normalizeDepartments, data])

  const title = useMemo(() => {
    let value = `Habilitar ${CONVERSION_EVENTS_OBJECT[eventType].label}:`
    if (pathname.includes('edit')) {
      value = value.replace('Habilitar', 'Editar')
    }
    return value
  }, [pathname, eventType])

  const { createSnackbar } = useSnackbar()

  useEffect(() => {
    try {
      if (eventDetails && pathname.includes('edit')) {
        if (eventDetails.departments.length) {
          if (eventDetails.departments.length === departmentsList.length) {
            setDepartments([allDeps])
          } else {
            const deps = []
            eventDetails.departments.forEach((department) => {
              deps.push(departmentsList.filter((item) => item.uuid === department.departmentId)[0])
            })
            setDepartments(deps)
          }
        }
      }
    } catch (error) {
      console.error(error)
    }
  }, [departmentsList.length, eventDetails, setDepartments, pathname])

  useEffect(() => {
    if (isSuccess) {
      navigate(eventsListPath, {
        replace: true,
      })
    }
    if (isError) {
      createSnackbar('Não foi possível salvar o evento. Tente novamente!')
    }
  }, [isError, isSuccess])

  const handleCancel = () => {
    navigate(eventsListPath)
  }

  const handleSubmit = async () => {
    let department_ids = []
    const channelsSelected = channels.map((item) => item.id)
    if (departments.some((item) => item.id === -1)) {
      department_ids = departmentsList.map((item) => item.uuid)
    } else {
      department_ids = departments.map((item) => item.uuid)
    }

    try {
      await mutateAsync({ channels: channelsSelected, department_ids, event_id: Number(eventId) })
    } catch (error) {
      console.error(error)
    }
  }

  const handleDepartmentSelect = (event, newValue) => {
    if (newValue.some((item) => item.id === -1) || newValue.length === departmentsList.length) {
      setDepartments([allDeps])
    } else {
      setDepartments(newValue)
    }
  }

  if (loadingDepartments) return <Skeletons.EditEvent />

  return (
    <Stack
      sx={{
        maxWidth: '50rem',
        height: 'auto',
        margin: '1.5rem auto',
        gap: '1.5rem',
      }}
      data-testid="edit-event-container"
    >
      <Link
        style={{
          color: theme.palette.primary.main,
        }}
        data-testid="back-btn"
        relative="path"
        to={eventsListPath}
      >
        <Tooltip title="Voltar">
          <IconButton>
            <ArrowBack />
          </IconButton>
        </Tooltip>
      </Link>
      <Stack flexDirection="row" gap=".5rem">
        <Typography sx={{ fontSize: '2rem', fontWeight: '900', lineHeight: 1 }}>{title}</Typography>
        <Typography
          sx={(theme) => ({
            fontSize: '2rem',
            fontWeight: '900',
            lineHeight: 1,
            color: theme.palette.colors.secondaryText,
          })}
        >
          {state.name}
        </Typography>
      </Stack>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '2.5rem',
          padding: '0 5rem',
          width: 'auto',
          maxWidth: '45rem',
        }}
      >
        <Alert severity="warning" sx={{ alignItems: 'center', color: 'warning' }}>
          <AlertTitle>Importante</AlertTitle>
          <Typography variant="body2">
            Neste momento, apenas departamentos com o Hi Bot podem ser utilizados no rastreamento de eventos e
            conversões.
          </Typography>
        </Alert>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '2.5rem',
          padding: '0 5rem',
          width: 'auto',
        }}
      >
        <Stack>
          <Typography
            variant="h6"
            sx={(theme) => ({
              color: theme.palette.colors.mainText,
              fontSize: '1.2rem',
              fontWeight: 700,
            })}
          >
            Atribuir departamentos
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: 'flex',
              color: theme.palette.colors.secondaryText,
              gap: '.3rem',
              mb: '1.5rem',
            }}
          >
            Habilite este evento nos departamentos que deseja mapeá-los.
            <Link
              style={{
                color: theme.palette.primary.main,
              }}
              to="/"
              replace
            >
              Saiba mais
            </Link>
          </Typography>
          <Autocomplete
            multiple
            limitTags={2}
            loading={loadingDetails || loadingDepartments}
            disabled={loadingDepartments}
            id="multiple-departments"
            options={[allDeps, ...departmentsList]}
            getOptionLabel={(option) => option?.label}
            getOptionDisabled={() => departments.some((item) => item.id === -1)}
            onChange={handleDepartmentSelect}
            value={departments || []}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Selecione os departamentos para este evento*"
                placeholder="Selecione os departamentos"
              />
            )}
            fullWidth
            sx={{ maxWidth: '35rem' }}
            data-testid="multiple-departments"
            noOptionsText="Sem opções"
          />
        </Stack>
        <Stack>
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.colors.mainText,
              fontSize: '1.2rem',
              fontWeight: 700,
            }}
          >
            Canais de rastreamento
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: 'flex',
              color: theme.palette.colors.secondaryText,
              gap: '.3rem',
              mb: '1.5rem',
            }}
          >
            Escolha canais Hi Platform que deseja utilizar este evento de rastreamento.
            <Link
              style={{
                color: theme.palette.primary.main,
              }}
              to="/"
              replace
            >
              Saiba mais
            </Link>
          </Typography>
          <Autocomplete
            multiple
            limitTags={2}
            id="multiple-channels"
            options={channels}
            getOptionLabel={(option) => option.name}
            value={channels}
            renderInput={(params) => <TextField {...params} label="Selecione os canais Hi que deseja*" />}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index })
                return (
                  <Chip
                    sx={{
                      bgcolor: '#AAA',
                      fontWeight: 'bold',
                    }}
                    key={key}
                    label={option.name}
                    {...tagProps}
                  />
                )
              })
            }
            fullWidth
            disabled
            disableClearable
            disableListWrap
            disabledItemsFocusable
            sx={{ maxWidth: '35rem' }}
            data-testid="multiple-channels"
          />
        </Stack>
      </Box>
      {eventType === 'conversion' ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2.5rem',
            padding: '0 5rem',
            width: 'auto',
            maxWidth: '45rem',
          }}
        >
          <Alert severity="warning" sx={{ alignItems: 'center', color: 'warning' }}>
            <AlertTitle>Importante</AlertTitle>
            <Typography variant="body2">
              Após habilitar e ativar esta conversão, será necessário atribuir um valor a ela, de forma manual ou
              dinâmica, utilizando o bloco <strong>Variável de Registro</strong> nos fluxos de conteúdo do bot.
            </Typography>
          </Alert>
        </Box>
      ) : null}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'right',
          gap: '2.5rem',
          maxWidth: '45rem',
        }}
      >
        <Button
          variant="outlined"
          onClick={handleCancel}
          disabled={isLoading}
          sx={{
            color: '#00AEEF',
          }}
        >
          Cancelar
        </Button>
        <Button
          sx={{
            bgcolor: '#00AEEF',
          }}
          disabled={isLoading || !channels?.length || !departments?.length || loadingDepartments}
          variant="contained"
          onClick={handleSubmit}
          endIcon={
            isLoading ? (
              <CircularProgress
                sx={{
                  color: 'currentcolor',
                }}
                size={20}
              />
            ) : null
          }
        >
          Salvar
        </Button>
      </Box>
    </Stack>
  )
}

export default EditEvents
